import React from 'react';
import { TextField, MenuItem, Select, FormControl, InputLabel, Box } from '@mui/material';
import { toNepaliDate } from './nepaliDate';

/**
 * Returns an array of Nepali months mapped from 1 to 12
 * Used for dropdowns and filtering
 */
export const getNepaliMonthOptions = () => {
    return [
        { value: "01", label: 'Baishakh' },
        { value: "02", label: 'Jestha' },
        { value: "03", label: 'Ashadh' },
        { value: "04", label: 'Shrawan' },
        { value: "05", label: 'Bhadra' },
        { value: "06", label: 'Ashwin' },
        { value: "07", label: 'Kartik' },
        { value: "08", label: 'Mangsir' },
        { value: "09", label: 'Poush' },
        { value: "10", label: 'Magh' },
        { value: "11", label: 'Falgun' },
        { value: "12", label: 'Chaitra' }
    ];
};

/**
 * Returns an array of Nepali years for dropdowns
 */
export const getNepaliYearOptions = () => {
    const currentYear = 2083; // Current BS year roughly
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 10; i++) {
        years.push(i);
    }
    return years;
};

/**
 * A custom date input component that uses a text field with YYYY-MM-DD format
 * Does NOT use any external datepicker library
 */
export const CustomNepaliDatePicker = ({ value, onChange, name, label, placeholder }) => {
    const handleTextChange = (e) => {
        let val = e.target.value;
        // Simple auto-formatting for YYYY-MM-DD
        if (val.length === 4 && !val.includes('-')) {
            val += '-';
        } else if (val.length === 7 && val.split('-').length === 2) {
            val += '-';
        }

        // Limit length
        if (val.length <= 10) {
            onChange(val);
        }
    };

    return (
        <TextField
            fullWidth
            label={label || "Date (YYYY-MM-DD)"}
            placeholder={placeholder || "2083-01-15"}
            value={value || ''}
            onChange={handleTextChange}
            variant="outlined"
            size="small"
            helperText="Format: YYYY-MM-DD"
        />
    );
};

/**
 * Helper to get month name from YYYY-MM-DD string
 */
export const getMonthNameFromDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return '';
    const parts = dateString.split('-');
    if (parts.length >= 2) {
        const monthNum = parts[1];
        const month = getNepaliMonthOptions().find(m => m.value === monthNum);
        return month ? month.label : '';
    }
    return '';
};

/**
 * Get current BS date in YYYY-MM-DD format
 */
export const getCurrentBSDate = () => {
    const bs = toNepaliDate(new Date());
    if (!bs) return "";
    const mm = bs.month < 10 ? `0${bs.month}` : bs.month;
    const dd = bs.day < 10 ? `0${bs.day}` : bs.day;
    return `${bs.year}-${mm}-${dd}`;
};

/**
 * Strips time from ISO date strings, returning only YYYY-MM-DD
 */
export const formatDatePart = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return dateString;
    return dateString.split('T')[0];
};

/**
 * Formats a date (Gregorian Date, ISO string, or BS date string) to BS YYYY-MM-DD
 */
export const formatToBSDate = (dateVal) => {
    if (!dateVal) return "";
    
    // If it's already a BS date string like YYYY-MM-DD where YYYY is a BS year (e.g. 2075-2095)
    if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
        const year = parseInt(dateVal.split('-')[0], 10);
        if (year >= 2070 && year <= 2100) {
            return dateVal;
        }
    }
    
    // Otherwise, treat as Gregorian and convert to BS
    try {
        const adDate = new Date(dateVal);
        if (isNaN(adDate.getTime())) return "";
        const bs = toNepaliDate(adDate);
        if (!bs) return "";
        const mm = bs.month < 10 ? `0${bs.month}` : bs.month;
        const dd = bs.day < 10 ? `0${bs.day}` : bs.day;
        return `${bs.year}-${mm}-${dd}`;
    } catch (e) {
        return "";
    }
};

export default {
    getNepaliMonthOptions,
    getNepaliYearOptions,
    CustomNepaliDatePicker,
    getMonthNameFromDate,
    getCurrentBSDate,
    formatDatePart,
    formatToBSDate
};

